<template>
  <ak-sidebar-layout sidebar-right size="large" class="ak-builder">
    <template #body>
      <div class="h-full flex flex--column overflow-hidden bg-white relative">
        <ak-loading
          v-model:active="isTemplateLoading"
          :can-cancel="false"
          :is-full-page="false"
        />

        <div id="builder-toolbar" class="ak-builder__toolbar has-shadow"></div>

        <div
          v-if="hasTemplate"
          class="layout-builder-wrap__body-inner is-scrollable h-full py"
          :style="backgroundStyles"
        >
          <ak-draggable
            id="templateContainer"
            v-model="templateModel.blocks"
            item-key="uuid"
            class="ak-builder__content"
            :class="{
              'ak-builder__content--empty': !hasTemplateBlocks,
            }"
            group="blocks"
            handle="[data-draggable-handle]"
          >
            <template #item="{ element, index }">
              <ak-layout-builder :key="element.uuid">
                <template #actions>
                  <ak-tooltip theme="light" placement="bottom">
                    <template #trigger>
                      <ak-button
                        color="grey-dark"
                        circle
                        @click.prevent="editBlockSettings(element, index)"
                      >
                        <ak-icon type="cog" />
                      </ak-button>
                    </template>
                    Block Options
                  </ak-tooltip>
                </template>
                <template #actionsExpanded>
                  <ak-tooltip theme="light" placement="bottom" class="mr-tiny">
                    <template #trigger>
                      <ak-button
                        color="danger"
                        circle
                        @click.prevent="confirmRemoveBlock(element, index)"
                      >
                        <ak-icon type="trash" />
                      </ak-button>
                    </template>
                    Remove Block
                  </ak-tooltip>
                  <ak-tooltip theme="light" placement="bottom">
                    <template #trigger>
                      <ak-button
                        color="grey-dark"
                        circle
                        @click.prevent="cloneBlock(element, index)"
                      >
                        <ak-icon type="clone" />
                      </ak-button>
                    </template>
                    Clone
                  </ak-tooltip>
                </template>
                <ak-email-template-block
                  :model-value="element"
                  :settings="templateModel.settings"
                  :opts="templateModel.opts"
                  :block-index="index"
                  :drawer-container="drawerContainer"
                  is-builder
                />
              </ak-layout-builder>
            </template>
          </ak-draggable>
          <div
            style="margin: 0px auto; max-width: 600px"
            :style="backgroundStyles"
          >
            <table
              align="center"
              border="0"
              cellpadding="0"
              cellspacing="0"
              role="presentation"
            >
              <tbody>
                <tr>
                  <td
                    style="
                      direction: ltr;
                      font-size: 0px;
                      padding: 30px 0;
                      text-align: center;
                    "
                  >
                    <a data-ignore="shortener" :style="unsubStyles">
                      Unsubscribe from this list
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
    <template #sidebar>
      <ak-sidebar id="sidebar" big class="relative bg-grey-dark">
        <ak-tabs
          v-model="activeTab"
          nav-background="grey-darker"
          nav-color="white"
          class="h-full"
          justified
        >
          <ak-tab-item label="Blocks" class="is-scrollable">
            <ak-box size="small" class="is-scrollable">
              <ak-draggable
                v-model="blocks"
                :group="{
                  name: 'blocks',
                  pull: 'clone',
                  put: false,
                }"
                :sort="false"
                :clone="onDrop"
                item-key="type"
              >
                <template #item="{ element, index }">
                  <ak-card :key="index" class="mb-small">
                    <ak-card-image :src="element.preview" />
                    <ak-card-body size="small" narrow>
                      <div class="text-small">{{ element.title }}</div>
                    </ak-card-body>
                  </ak-card>
                </template>
              </ak-draggable>
            </ak-box>
          </ak-tab-item>
          <ak-tab-item label="Design" class="is-scrollable">
            <ak-box class="color-white">
              <ak-field label="Title Font">
                <ak-select
                  :model-value="selectedHeadingsFont"
                  :options="fonts"
                  @select="onSelectHeadingsFont"
                >
                  <template #singleLabel="{ option }">
                    <span :style="{ 'font-family': option.value }">
                      {{ option.label }}
                    </span>
                  </template>
                  <template #option="{ option }">
                    <span :style="{ 'font-family': option.value }">
                      {{ option.label }}
                    </span>
                  </template>
                </ak-select>
              </ak-field>

              <ak-field label="Body Font">
                <ak-select
                  :model-value="selectedBodyFont"
                  :options="fonts"
                  @select="onSelectBodyFont"
                >
                  <template #singleLabel="{ option }">
                    <span :style="{ 'font-family': option.value }">
                      {{ option.label }}
                    </span>
                  </template>
                  <template #option="{ option }">
                    <span :style="{ 'font-family': option.value }">
                      {{ option.label }}
                    </span>
                  </template>
                </ak-select>
              </ak-field>

              <ak-field label="Background Color">
                <ak-color-picker
                  :color="templateModel.settings.background"
                  placement="bottom"
                  @change-color="changeBackgroundColor"
                />
              </ak-field>
            </ak-box>
          </ak-tab-item>
        </ak-tabs>
        <ak-drawer
          v-model:active="drawerActive"
          show-x
          container="#sidebar"
          inset="left"
          has-backdrop
        >
          <template #body>
            <div v-if="templateModel.blocks[selectedBlockIndex]">
              <ak-modal-title>Edit {{ selectedBlockTitle }}</ak-modal-title>
              <ak-field label="Background Color">
                <ak-color-picker
                  :color="
                    templateModel.blocks[selectedBlockIndex].settings.background
                  "
                  placement="bottom"
                  @change-color="changeBlockBackgroundColor"
                />
              </ak-field>

              <ak-field label="Column Alignment">
                <ak-radio-group
                  v-model="
                    templateModel.blocks[selectedBlockIndex].settings
                      .columnAlignment
                  "
                  :options="alignOpts"
                  vertical
                  name="columnAlignment"
                >
                </ak-radio-group>
              </ak-field>

              <ak-field label="Spacing">
                <ak-switch
                  v-model="
                    templateModel.blocks[selectedBlockIndex].settings
                      .flushSpacing
                  "
                >
                  Edge to edge
                </ak-switch>
              </ak-field>
            </div>
          </template>
        </ak-drawer>
      </ak-sidebar>
    </template>
  </ak-sidebar-layout>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue'
import Block from '../block/Block.vue'
import Draggable from '@akerolabs/akero-vue/src/components/draggable/Draggable'
import SidebarLayout from '@akerolabs/akero-vue/src/components/sidebar/SidebarLayout.vue'
import Sidebar from '@akerolabs/akero-vue/src/components/sidebar/Sidebar.vue'
import Navbar from '@akerolabs/akero-vue/src/components/navbar/Navbar.vue'
import NavbarItem from '@akerolabs/akero-vue/src/components/navbar/NavbarItem.vue'
import NavbarButton from '@akerolabs/akero-vue/src/components/navbar/NavbarButton.vue'
import Loading from '@akerolabs/akero-vue/src/components/loading/Loading.vue'
import Tabs from '@akerolabs/akero-vue/src/components/tabs/Tabs.vue'
import TabItem from '@akerolabs/akero-vue/src/components/tabs/TabItem.vue'
import Box from '@akerolabs/akero-vue/src/components/box/Box.vue'
import Icon from '@akerolabs/akero-vue/src/components/icon/Icon.vue'
import Button from '@akerolabs/akero-vue/src/components/button/Button.vue'
import Field from '@akerolabs/akero-vue/src/components/field/Field.vue'
import Select from '@akerolabs/akero-vue/src/components/select/Select.vue'
import ColorPicker from '@akerolabs/akero-vue/src/components/colorpicker/ColorPicker.vue'
import Drawer from '@akerolabs/akero-vue/src/components/drawer/Drawer.vue'
import { isObject } from '@akerolabs/akero-vue/src/utils/helpers.js'
import config from '../../utils/config'
import contrastColor from '../../utils/contrastColor'
import { DialogProgrammatic } from '@akerolabs/akero-vue/src/components/dialog'
import { defaultFonts } from '../../utils/config'

import {
  uuid,
  merge,
  rgbaToString,
} from '@akerolabs/akero-vue/src/utils/helpers'

const clone = (o) => JSON.parse(JSON.stringify(o))

const defaultAlignOpts = [
  {
    label: 'Top',
    value: 'top',
  },
  {
    label: 'Middle',
    value: 'middle',
  },
  {
    label: 'Bottom',
    value: 'bottom',
  },
]

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-email-template-builder></ak-email-template-builder>
   */
  name: 'AkEmailTemplateBuilder',

  /**
   * @type {Boolean}
   * @description Causes the vue application to preserve HTML comments
   */
  comments: true,

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Block.name]: Block,
    [Draggable.name]: Draggable,
    [NavbarButton.name]: NavbarButton,
    [Navbar.name]: Navbar,
    [NavbarItem.name]: NavbarItem,
    [SidebarLayout.name]: SidebarLayout,
    [Sidebar.name]: Sidebar,
    [Loading.name]: Loading,
    [Tabs.name]: Tabs,
    [TabItem.name]: TabItem,
    [Box.name]: Box,
    [Icon.name]: Icon,
    [Button.name]: Button,
    [Field.name]: Field,
    [ColorPicker.name]: ColorPicker,
    [Select.name]: Select,
    [Drawer.name]: Drawer,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Object}
     * @description The template object that we want to edit
     */
    modelValue: {
      type: Object,
      default: () => ({
        title: 'Blank',
        settings: { ...config.defaultTemplateStyles },
        blocks: [],
      }),
    },

    /**
     * @type {Boolean}
     * @description determines if we are loading the template, this is passed
     * in from the parent, as that will handle the loading of the template
     * data
     */
    isLoading: {
      type: Boolean,
      default: true,
    },

    /**
     * @type {String}
     * @description The container in which to position the drawers
     */
    drawerContainer: {
      type: String,
      default: null,
    },
  },

  emits: ['update:modelValue', 'update:isLoading'],

  /**
   * @type {Function}
   * @returns {Object}
   * @description Returns the default state of the component.
   */
  setup(props, { emit }) {
    const blocks = ref([...config.blocks])
    const activeTab = ref(0)
    const drawerActive = ref(false)
    const selectedBlockIndex = ref(null)
    const fonts = ref(defaultFonts)
    const alignOpts = ref(defaultAlignOpts)

    const templateModel = computed({
      get() {
        return props.modelValue
      },
      set(val) {
        emit('update:modelValue', val)
      },
    })

    const isTemplateLoading = computed({
      get() {
        return props.isLoading
      },
      set(val) {
        emit('update:isLoading', val)
      },
    })

    const selectedHeadingsFont = computed(() => {
      return fonts.value.find(
        (o) => o.value === templateModel.value.settings.headingsFont,
      )
    })

    const selectedBodyFont = computed(() => {
      return fonts.value.find(
        (o) => o.value === templateModel.value.settings.bodyFont,
      )
    })

    const backgroundStyles = computed(() => {
      return {
        background:
          templateModel.value &&
          templateModel.value.settings &&
          templateModel.value.settings.background,
      }
    })

    const unsubStyles = computed(() => {
      return {
        fontSize: '13px',
        fontFamily: templateModel.value.settings.bodyFont,
        textDecoration: 'underline',
        color: contrastColor(templateModel.value.settings.background),
      }
    })

    const selectedBlockTitle = computed(() => {
      return templateModel.value.blocks[selectedBlockIndex.value].title
    })

    const hasTemplateBlocks = computed(() => {
      return (
        Array.isArray(templateModel.value.blocks) &&
        templateModel.value.blocks.length >= 0
      )
    })

    const hasTemplateSettings = computed(() => {
      return isObject(templateModel.value.settings)
    })

    const hasTemplate = computed(() => {
      return hasTemplateBlocks.value && hasTemplateSettings.value
    })

    /**
     * @param {Object} block
     * @param {Number} blockIndex
     * @description Triggers the remove block dialog to ask the user is they are
     * sure that they want to remove the block before we delete it.
     */
    const confirmRemoveBlock = (block, blockIndex) => {
      DialogProgrammatic.confirm({
        title: 'Removing Block',
        message: 'Are you sure you want to <strong>remove</strong> this block?',
        confirmText: 'Yes, remove block',
        cancelText: 'No, keep block',
        type: 'danger',
        onConfirm: () => removeBlock(block, blockIndex),
      })
    }

    /**
     * @param {Object} block
     * @param {Number} blockIndex
     * @description Removes a block from the template.blocks array.
     */
    const removeBlock = (block, blockIndex) => {
      if (block && typeof blockIndex === 'number') {
        displayLoader()
        templateModel.value.blocks.splice(blockIndex, 1)
      }
    }

    /**
     * @param {Object} block
     * @param {Number} blockIndex
     * @description Triggers the remove block dialog to ask the user is they are
     * sure that they want to remove the block before we delete it.
     */
    const editBlockSettings = (block, blockIndex) => {
      drawerActive.value = true
      if (block && typeof blockIndex === 'number') {
        selectedBlockIndex.value = blockIndex
      }
    }

    /**
     * @param {Object} block
     * @param {Number} index
     * @description Clones an existing block
     */
    const cloneBlock = (block, index) => {
      const newBlock = merge(clone(block), { uuid: uuid() }, true)
      templateModel.value.blocks.splice(index, 0, newBlock)
      displayLoader()
    }

    /**
     * @param {Object} color
     * @description Sets the background color for the email template.
     */
    const changeBackgroundColor = (color) => {
      const newColor = rgbaToString(color)
      templateModel.value.settings.background = newColor
      displayLoader()
    }

    /**
     * @param {Object} color
     * @description Sets the background color for  a block in the
     * email template.
     */
    const changeBlockBackgroundColor = (color) => {
      if (typeof selectedBlockIndex.value === 'number') {
        const newColor = rgbaToString(color)
        const block = templateModel.value.blocks[selectedBlockIndex.value]
        if (block.settings.background !== newColor) {
          block.settings.background = rgbaToString(color)
          displayLoader()
        }
      }
    }

    /**
     * @description Quickly show the loader to try prevent sync bugs in
     * the drag and drop template display.
     */
    const displayLoader = () => {
      isTemplateLoading.value = true
      setTimeout(() => {
        isTemplateLoading.value = false
      }, 500)
    }

    const selectedFontLabel = ({ label }) => {
      return label
    }

    const onSelectHeadingsFont = (selected) => {
      templateModel.value.settings.headingsFont = selected.value
    }

    const onSelectBodyFont = (selected) => {
      templateModel.value.settings.bodyFont = selected.value
    }

    const onDrop = (block) => {
      return merge(clone(block), { uuid: uuid() }, true)
    }

    watch(
      () => props.isLoading,
      (val) => {
        isTemplateLoading.value = val
      },
    )

    return {
      blocks,
      templateModel,
      isTemplateLoading,
      activeTab,
      drawerActive,
      selectedBlockIndex,
      fonts,
      alignOpts,
      selectedHeadingsFont,
      selectedBodyFont,
      backgroundStyles,
      unsubStyles,
      selectedBlockTitle,
      confirmRemoveBlock,
      removeBlock,
      editBlockSettings,
      cloneBlock,
      changeBackgroundColor,
      changeBlockBackgroundColor,
      displayLoader,
      selectedFontLabel,
      onSelectHeadingsFont,
      onSelectBodyFont,
      onDrop,
      hasTemplateBlocks,
      hasTemplateSettings,
      hasTemplate,
    }
  },
})
</script>

<style lang="scss" rel="stylesheet/scss">
@import '@akerolabs/akero-vue/src/scss/_settings.scss';
body {
  .ak-custom-html {
    .ak-modal {
      z-index: 9999;
    }
  }
}
.ak-builder {
  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__content {
    &:empty {
      min-height: 200px;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: rgba(255, 255, 255, 0.5);
      width: 100%;
      max-width: 600px;
      margin-left: auto;
      margin-right: auto;
      font-size: 125%;
      &::after {
        content: 'Drag a block to start building';
      }
    }
    .sortable-ghost {
      max-width: 600px;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: -10px; //hide the email block spacing

      &::after {
        font-size: 125%;
        display: block;
        content: 'Drop here';
        color: $color-primary;
        font-weight: bold;
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      * {
        opacity: 0;
        visibility: hidden;
      }
    }
  }
}
#templateContainer {
  #outlook a {
    padding: 0;
  }

  table,
  td {
    border-collapse: collapse;
    mso-table-lspace: 0pt;
    mso-table-rspace: 0pt;
  }

  img {
    border: 0;
    line-height: 100%;
    outline: none;
    text-decoration: none;
    -ms-interpolation-mode: bicubic;
    max-width: 100%;
  }

  p {
    display: block;
  }

  @media screen and (max-width: 480px) and (max-device-width: 480px) {
    .c-email-template__columns {
      display: block !important;
    }
    .c-email-template__column {
      display: block !important;
      width: 100% !important;
    }
  }
}
</style>
